import {
  TeamReportCellPart,
  TeamReportDataCell,
  TeamReportDataTableProps,
} from "./types";
import {
  CELL_SAMPLE_FLOORS,
  CRASH_OE_HEADERS,
  REBOUND_VALUE_HEADERS,
  SIGNED_PER_CHANCE_HEADERS,
  SMALL_SAMPLE_POSS_THRESHOLD,
  VOLUME_HEADERS,
} from "../constants";
import { formatSigned } from "../../util/formatSigned";

// A x100 chance-PPP cell shown per chance ("1.08"), floored at 0.00 unless the column is a signed margin
// (SIGNED_PER_CHANCE_HEADERS), which shows its sign ("+0.12", RV1). Also the composite cells' per-chance subValue
// (header omitted: always floored).
export const perChancePPPText = (value: number, header?: string, scale = 100): string => {
  const v = Number(value) / scale;
  return header && SIGNED_PER_CHANCE_HEADERS.has(header)
    ? formatSigned(v, 2)
    : Math.max(0, v).toFixed(2);
};

// The decimals of an explicitly signed column (RV1, Trevor 2026-10-16), or undefined when the column isn't signed.
// A game's rebounding value columns are counts (1 decimal), a season's rates per 100 misses (2); crash OE is
// percentage points (1). The margins sign in perChancePPPText; this covers them only on a table shown per 100.
export const signedDecimals = (
  header: string,
  singleGame?: boolean
): number | undefined =>
  REBOUND_VALUE_HEADERS.has(header)
    ? singleGame
      ? 1
      : 2
    : CRASH_OE_HEADERS.has(header) || SIGNED_PER_CHANCE_HEADERS.has(header)
      ? 1
      : undefined;

// One hover-breakdown line's value: a count out of its total ("12 of 33"), a share ("34%"), else signed like its cell.
export const partText = (part: TeamReportCellPart, decimals: number): string =>
  part.of !== undefined && part.of !== null
    ? `${part.value} of ${part.of}`
    : part.unit === "%"
      ? `${Math.round(Number(part.value))}%`
      : formatSigned(Number(part.value), decimals);

// A rate cell under its column's sample floor (CELL_SAMPLE_FLOORS): only cells
// that carry their sample (`n`) are judged.
export const isLowSampleCell = (
  header: string,
  cell?: TeamReportDataCell
): boolean => {
  const floor = CELL_SAMPLE_FLOORS[header];
  return floor !== undefined && cell?.n !== undefined && cell.n < floor.min;
};

// The caption line for a table whose cells fell under a floor: "Grey 3P%:
// fewer than 15 3PA (no percentile)".
export const lowSampleCellNotes = (
  headers: string[],
  rows: { cells: Record<string, TeamReportDataCell> }[]
): string[] =>
  headers
    .filter((h) => rows.some((row) => isLowSampleCell(h, row.cells[h])))
    .map(
      (h) =>
        `Grey ${h}: fewer than ${CELL_SAMPLE_FLOORS[h].min} ${CELL_SAMPLE_FLOORS[h].unit} (no percentile)`
    );

// The row's volume: its possessions, or the action / chance count the action
// and start-type tables show instead (TR5).
const volumeCell = (
  cells: Record<string, TeamReportDataCell>
): TeamReportDataCell | undefined =>
  VOLUME_HEADERS.map((h) => cells[h]).find((cell) => cell !== undefined);

// The unit the sample captions name: the first volume column the table shows.
export const volumeUnit = (headers?: string[]): string => {
  const header = VOLUME_HEADERS.find((h) => headers?.includes(h));
  return header === "Actions"
    ? "actions"
    : header === "Chances"
      ? "chances"
      : "possessions";
};

// Rank presence (vs percentile) is what distinguishes season tables from game
// tables at the cell level, so this self-gates to season reports.
export const isSmallSampleRow = (
  cells: Record<string, TeamReportDataCell>
): boolean =>
  (volumeCell(cells)?.value ?? Infinity) < SMALL_SAMPLE_POSS_THRESHOLD &&
  Object.values(cells).some((cell) => cell.rank !== undefined);

// A table-level possession floor (single-game tables, where the season
// fade's rank gate never fires). A row without a volume count is exempt;
// an empty count is zero.
export const isBelowSampleFloor = (
  cells: Record<string, TeamReportDataCell>,
  floor?: number
): boolean => {
  const cell = volumeCell(cells);
  return floor !== undefined && cell !== undefined && (cell.value ?? 0) < floor;
};

export const hasAnyShotDistribution = (
  rows: TeamReportDataTableProps["rows"]
): boolean => {
  if (!rows || !Array.isArray(rows)) return false;
  return rows.some(
    (row) =>
      row.shotDistribution && Object.keys(row.shotDistribution).length > 0
  );
};

const RowLabelOverrides: Record<string, string> = {
  Drive: "Miscellaneous Drives",
  // The table splits every attempt, made or missed, by whether it came off a pass (AssistOpportunity), not
  // official assists (Trevor, 2026-10-10)
  Assisted: "Assisted attempts",
  Unassisted: "Unassisted attempts",
};

const StartTypeLabelMap: Record<string, string> = {
  "db/aft": "Dead Ball",
  db: "Dead Ball",
  aft: "AFT",
  tov: "Live Ball TOV",
  ato: "ATO",
  orb: "ORB",
};

const CompactRowLabelMap: Record<string, string> = {
  "above the break three": "ATB 3",
  "corner three": "Corner 3",
  tov: "TOV",
};

export const getReadableRowLabel = (
  label: React.ReactNode,
  compactRowLabels = false
): React.ReactNode => {
  if (typeof label === "string") {
    if (RowLabelOverrides[label]) {
      return RowLabelOverrides[label];
    }
    if (compactRowLabels && CompactRowLabelMap[label]) {
      return CompactRowLabelMap[label];
    }
    if (StartTypeLabelMap[label]) {
      return StartTypeLabelMap[label];
    }
    if (label === label.toLowerCase()) {
      if (label === "ato" || label === "orb") {
        return label.toUpperCase();
      }
      return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
    }
  }
  return label;
};

export const commonRenderValue = (value?: number): React.ReactNode => {
  if (value === undefined) return null;
  if (Math.abs(value) < 5) {
    return value.toFixed(2);
  }
  return value.toFixed(1);
};

export const hasNoCounts = (
  cells: Record<string, TeamReportDataCell>
): boolean => {
  return Object.values(cells).every((cell) => cell.value === 0);
};
